import { getAuthToken } from './useAdminAuth';

const LOCAL_KEY   = 'ck-admin-events-draft';

/** Base URL for the admin API (see useAdminAuth.js for notes). */
const API_BASE    = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const API_EVENTS  = `${API_BASE}/api/events`;
const API_HEALTH  = `${API_BASE}/api/health`;
const API_VIDEOS_REFRESH = `${API_BASE}/api/videos/refresh`;
const STATIC_URL  = process.env.REACT_APP_EVENTS_URL || '/assets/eventos.json';

/* ============================================================
   Data model — GeoJSON FeatureCollection
     properties: { date (MM/DD/YYYY), title, address, city, country?,
                   image_url, ticket_url, vipticket_url }
     geometry:   { type: 'Point', coordinates: [lng, lat] }
   ============================================================ */

export function emptyCollection() {
  return { type: 'FeatureCollection', features: [] };
}

export function emptyEvent() {
  return {
    type: 'Feature',
    properties: {
      date: '',
      title: '',
      address: '',
      city: '',
      country: '',
      image_url: '',
      ticket_url: '',
      vipticket_url: '',
    },
    geometry: { type: 'Point', coordinates: [0, 0] },
  };
}

/* ── Server detection ────────────────────────────────────────── */
let _serverAvailable = null;

export async function pingServer() {
  try {
    const r = await fetch(API_HEALTH, { method: 'GET', cache: 'no-store' });
    _serverAvailable = r.ok;
    return _serverAvailable;
  } catch {
    _serverAvailable = false;
    return false;
  }
}

export function isServerAvailable() { return _serverAvailable === true; }

/* ── Load ────────────────────────────────────────────────────── */
async function loadFromApi() {
  const r = await fetch(API_EVENTS, { cache: 'no-store' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

async function loadFromStatic() {
  const r = await fetch(STATIC_URL, { cache: 'no-store' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

/** Load the current events, preferring the server (authoritative)
 *  and falling back to the static file if the server isn't running. */
export async function loadRemote() {
  let data;
  try {
    data = await loadFromApi();
    _serverAvailable = true;
  } catch {
    _serverAvailable = false;
    data = await loadFromStatic();
  }
  if (!data || data.type !== 'FeatureCollection' || !Array.isArray(data.features)) {
    throw new Error('Formato inválido');
  }
  return data;
}

/* ── Save ────────────────────────────────────────────────────── */
/** Try to save to the server. Returns { mode, count } on success,
 *  throws on failure. Only callers should catch + fall back to
 *  local draft. */
export async function saveRemote(collection) {
  const token = getAuthToken();
  if (!token) throw new Error('Sesión inválida, vuelve a iniciar sesión');

  const r = await fetch(API_EVENTS, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(collection),
  });

  if (r.status === 401) throw new Error('No autorizado (contraseña cambió)');
  if (!r.ok) throw new Error(`HTTP ${r.status}`);

  const data = await r.json().catch(() => ({}));
  _serverAvailable = true;
  return { mode: 'server', count: data.count || collection.features.length };
}

/* ── Videos (manual refresh from YouTube) ────────────────────── */
/** Asks the backend to re-scrape the YouTube channel and rewrite
 *  videos.json. Returns { ok, count, added: [{id,title}], savedAt }.
 *  Requires an active admin session; throws on failure. */
export async function refreshVideosRemote() {
  const token = getAuthToken();
  if (!token) throw new Error('Sesión inválida, vuelve a iniciar sesión');

  const r = await fetch(API_VIDEOS_REFRESH, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (r.status === 401) throw new Error('No autorizado (contraseña cambió)');
  const data = await r.json().catch(() => ({}));
  if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`);
  return data;
}

/* ── Local draft (fallback / offline) ────────────────────────── */
export function loadLocalDraft() {
  try {
    const raw = window.localStorage.getItem(LOCAL_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !Array.isArray(data.features)) return null;
    return data;
  } catch { return null; }
}

export function saveLocalDraft(collection) {
  try {
    window.localStorage.setItem(LOCAL_KEY, JSON.stringify(collection));
  } catch (e) { /* ignore (likely quota or private mode) */ }
}

export function clearLocalDraft() {
  try { window.localStorage.removeItem(LOCAL_KEY); } catch (e) { /* ignore */ }
}

/* ── Image upload (POST /api/upload, multipart/form-data) ────── */
/** Uploads a single image File to the backend. Returns
 *  { ok, url, name, size, mime }. Calls onProgress(percent 0-100)
 *  during the upload so the UI can show progress. Throws on error. */
export function uploadImage(file, onProgress) {
  const token = getAuthToken();
  if (!token) return Promise.reject(new Error('Sesión inválida'));

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const form = new FormData();
    form.append('image', file);

    if (typeof onProgress === 'function') {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      });
    }

    xhr.addEventListener('load', () => {
      let data = null;
      try { data = JSON.parse(xhr.responseText); } catch (e) { /* ignore */ }
      if (xhr.status >= 200 && xhr.status < 300 && data?.ok) {
        resolve(data);
      } else {
        reject(new Error(data?.error || `HTTP ${xhr.status}`));
      }
    });
    xhr.addEventListener('error', () => reject(new Error('Error de red')));
    xhr.addEventListener('abort', () => reject(new Error('Subida cancelada')));

    const url = `${API_BASE}/api/upload`;
    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(form);
  });
}

/* ── Site deploy (drag-and-drop a build .zip) ────────────────── */
/** Uploads a React build .zip to POST /api/deploy. The server extracts
 *  it into the web root, skipping api/, cron/ and uploads/ so events,
 *  videos and flyers are never touched. Calls onProgress(0-100). Returns
 *  { ok, deployed, skipped, protected, deployedAt }. Throws on error. */
export function deploySite(file, onProgress) {
  const token = getAuthToken();
  if (!token) return Promise.reject(new Error('Sesión inválida'));

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const form = new FormData();
    form.append('bundle', file);

    if (typeof onProgress === 'function') {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      });
    }

    xhr.addEventListener('load', () => {
      let data = null;
      try { data = JSON.parse(xhr.responseText); } catch (e) { /* ignore */ }
      if (xhr.status >= 200 && xhr.status < 300 && data?.ok) {
        resolve(data);
      } else {
        reject(new Error(data?.error || `HTTP ${xhr.status}`));
      }
    });
    xhr.addEventListener('error', () => reject(new Error('Error de red')));
    xhr.addEventListener('abort', () => reject(new Error('Despliegue cancelado')));

    xhr.open('POST', `${API_BASE}/api/deploy`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(form);
  });
}

/* ── JSON file download (manual publish) ─────────────────────── */
export function downloadJson(collection, filename = 'eventos.json') {
  const body = JSON.stringify(collection, null, 2) + '\n';
  const blob = new Blob([body], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ── Helpers ─────────────────────────────────────────────────── */
export function sortByDate(features) {
  const key = f => {
    const parts = String(f?.properties?.date || '').split('/');
    if (parts.length !== 3) return '9999-99-99';
    const [m, d, y] = parts;
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  };
  return [...features].sort((a, b) => key(a).localeCompare(key(b)));
}

/** Maps a country name (any common form) → ISO 3166-1 alpha-2 lowercase.
 *  Returns null if we don't recognize it. Used to bias geocoding. */
export function detectCountryCode(country) {
  const c = String(country || '').toLowerCase().trim();
  if (!c) return null;
  if (/^(mx|mex|m[ée]xico)$/.test(c) || c.includes('méxico') || c.includes('mexico')) return 'mx';
  if (/^(us|usa)$/.test(c) || c.includes('united states') || c.includes('estados unidos')) return 'us';
  if (/^(es)$/.test(c)   || c.includes('españa') || c.includes('spain'))   return 'es';
  if (/^(it)$/.test(c)   || c.includes('italia') || c.includes('italy'))   return 'it';
  if (/^(ca)$/.test(c)   || c.includes('canada') || c.includes('canadá'))  return 'ca';
  if (/^(co)$/.test(c)   || c.includes('colombia'))                        return 'co';
  if (/^(ar)$/.test(c)   || c.includes('argentina'))                       return 'ar';
  if (/^(cl)$/.test(c)   || c.includes('chile'))                           return 'cl';
  if (/^(pe)$/.test(c)   || c.includes('per[uú]'))                         return 'pe';
  return null;
}

/** Expands abbreviations common in Spanish-language addresses so the
 *  geocoder can resolve them. Patterns are written so the trailing period
 *  (when present) is consumed too — `\bWORD\b\.?` works because the `\b`
 *  matches between the last letter and the period (word/non-word border),
 *  then `\.?` optionally swallows the dot. */
export function expandSpanishAbbreviations(input) {
  let s = String(input || '');
  const map = [
    // Compass directions (street suffixes in numbered Mexican grids)
    [/\bPte\b\.?/gi,   'Poniente'],
    [/\bOte\b\.?/gi,   'Oriente'],
    [/\bNte\b\.?/gi,   'Norte'],
    // Street types
    [/\bAve?\b\.?/gi,  'Avenida'],
    [/\bBlvd\b\.?/gi,  'Boulevard'],
    [/\bBoul\b\.?/gi,  'Boulevard'],
    [/\bCalz\b\.?/gi,  'Calzada'],
    [/\bCarr\b\.?/gi,  'Carretera'],
    [/\bPriv\b\.?/gi,  'Privada'],
    [/\bAnd\b\.?/gi,   'Andador'],
    [/\bCda\b\.?/gi,   'Cerrada'],
    // Neighborhood / subdivision
    [/\bCol\b\.?/gi,   'Colonia'],
    [/\bFracc\b\.?/gi, 'Fraccionamiento'],
    [/\bSecc?\b\.?/gi, 'Sección'],
    [/\bU\.?\s?H\b\.?/g, 'Unidad Habitacional'],
    // Mexican state abbreviations (most common)
    [/\bPue\b\.?/gi,   'Puebla'],
    [/\bMéx\b\.?/gi,   'México'],
    [/\bMex\b\.?/gi,   'México'],
    [/\bN\b\.?\s?L\b\.?/gi, 'Nuevo León'],
    [/\bQ\b\.?\s?Roo\b/gi,  'Quintana Roo'],
    [/\bB\b\.?\s?C\b\.?/gi, 'Baja California'],
    [/\bD\b\.?\s?F\b\.?/gi, 'Ciudad de México'],
    [/\bCDMX\b/g,           'Ciudad de México'],
    [/\bGdl\b\.?/gi,        'Guadalajara'],
    [/\bMty\b\.?/gi,        'Monterrey'],
    // Spanish ordinals (common in colony names like "3ra Sección")
    [/\b1[ºªo]\b\.?/gi, 'Primera'],
    [/\b1ra?\b\.?/gi,   'Primera'],
    [/\b2[ºªo]\b\.?/gi, 'Segunda'],
    [/\b2da?\b\.?/gi,   'Segunda'],
    [/\b3[ºªo]\b\.?/gi, 'Tercera'],
    [/\b3ra?\b\.?/gi,   'Tercera'],
    [/\b4[ºªo]\b\.?/gi, 'Cuarta'],
    [/\b4ta?\b\.?/gi,   'Cuarta'],
    [/\b5[ºªo]\b\.?/gi, 'Quinta'],
    [/\b5ta?\b\.?/gi,   'Quinta'],
    // Av. is a 2-letter abbrev (above's `Ave?` covers it: matches "Av" + optional "e")
  ];
  for (const [re, repl] of map) s = s.replace(re, repl);
  return s.replace(/\s+/g, ' ').trim();
}

/** Strip the colonia / neighborhood from a comma-separated address so
 *  Nominatim has a better chance of finding the street. Keeps street,
 *  city, postcode (if present) and country/state. */
function simplifyAddress(input) {
  const parts = String(input).split(',').map(p => p.trim()).filter(Boolean);
  if (parts.length <= 2) return input;
  const first = parts[0];
  const last  = parts.slice(-2);
  const postcodePart = parts.slice(1, -2).find(p => /\b\d{4,6}\b/.test(p));
  const kept = postcodePart ? [first, postcodePart, ...last] : [first, ...last];
  // De-dupe while preserving order
  const seen = new Set();
  const dedup = kept.filter(p => { const k = p.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
  return dedup.join(', ');
}

async function nominatimSearch(q, countryCode) {
  const params = new URLSearchParams({
    format:           'jsonv2',
    limit:            '1',
    addressdetails:   '0',
    'accept-language': 'es,en',
    q,
  });
  if (countryCode) params.set('countrycodes', String(countryCode).toLowerCase());
  const url = `https://nominatim.openstreetmap.org/search?${params.toString()}`;
  const r = await fetch(url, { headers: { 'Accept': 'application/json' } });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const data = await r.json();
  return Array.isArray(data) && data.length ? data[0] : null;
}

/** OpenStreetMap Nominatim — free, no key, 1 req/sec politeness.
 *  Strategy:
 *    1. Expand common Spanish abbreviations (Pte→Poniente, 3ra→Tercera, …).
 *    2. Try the full expanded query.
 *    3. If 0 results, simplify (drop the colonia / neighborhood) and retry.
 *    4. As last resort, try just the first comma-separated part. */
export async function geocode(address, opts = {}) {
  const raw = String(address || '').trim();
  if (!raw) throw new Error('Dirección vacía');
  const cc = opts.countryCode ? String(opts.countryCode).toLowerCase() : '';

  const expanded = expandSpanishAbbreviations(raw);

  // Attempt 1 — expanded full query
  let hit = await nominatimSearch(expanded, cc);

  // Attempt 2 — drop the colonia / neighborhood part
  if (!hit) {
    const simplified = simplifyAddress(expanded);
    if (simplified !== expanded) {
      // Politeness delay (Nominatim asks for ≤1 req/sec)
      await new Promise(r => setTimeout(r, 1100));
      hit = await nominatimSearch(simplified, cc);
    }
  }

  // Attempt 3 — just the first chunk + country
  if (!hit) {
    const firstChunk = expanded.split(',')[0].trim();
    if (firstChunk && firstChunk !== expanded) {
      await new Promise(r => setTimeout(r, 1100));
      const last = opts.country || expanded.split(',').slice(-1)[0].trim();
      hit = await nominatimSearch(`${firstChunk}, ${last}`, cc);
    }
  }

  if (!hit) throw new Error('No encontramos esa dirección. Prueba con calle + ciudad solamente.');

  return {
    lat:     parseFloat(hit.lat),
    lng:     parseFloat(hit.lon),
    display: hit.display_name,
  };
}

export function validateEvent(feature) {
  const errs = [];
  const p = feature?.properties || {};
  if (!p.date || !/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(p.date)) errs.push('Fecha inválida (MM/DD/YYYY)');
  if (!p.title) errs.push('Falta el venue / título');
  if (!p.city)  errs.push('Falta la ciudad');
  const coords = feature?.geometry?.coordinates;
  if (!Array.isArray(coords) || coords.length !== 2) errs.push('Coordenadas inválidas');
  else {
    const [lng, lat] = coords;
    if (typeof lng !== 'number' || typeof lat !== 'number' || isNaN(lng) || isNaN(lat)) errs.push('Coordenadas inválidas');
  }
  return errs;
}
